import {
  UNIT_SUFFIXED_NUMBER_PATTERN,
  isDecimalZero,
  parseStrictDecimal,
  shiftPointRight,
  stripInvisibleEdges,
  toCanonicalString,
  truncateFraction,
  type DecimalParts,
} from "./decimal";
import { parseDateValue } from "./date";
import {
  parseEditedDurationValue,
  parseStoredDurationValue,
  serializeDurationParts,
} from "./duration";
import {
  isNumericKind,
  ok,
  reject,
  storedValue,
  type IXDStoredValue,
  type IXDViewValue,
  type NumericKind,
  type Result,
  type ValueContext,
} from "./types";

const isIntegerSubKind = (subKind?: string | null): boolean => {
  if (!subKind) return false;
  const base = subKind.replace(/ItemType$/, "").toLowerCase();
  return (
    base.includes("integer") || /^(unsigned)?(int|long|short|byte)$/.test(base)
  );
};

// nonNegative/unsigned 계열만 대상 — positiveInteger·negativeInteger 등
// 다른 범위 제약은 spec(05 §2) 범위 밖이라 여기서 강제하지 않는다.
const isNonNegativeSubKind = (subKind?: string | null): boolean => {
  if (!subKind) return false;
  const base = subKind.replace(/ItemType$/, "").toLowerCase();
  return base.includes("nonnegative") || base.startsWith("unsigned");
};

// toView와 사유 코드가 갈리지 않도록 직접 호출 경로에서도 동일한
// unit-suffix 판정을 적용한다. original은 받은 입력(view) 그대로 보존.
const rejectNumericParse = (
  stripped: string,
  original: string,
  fallback: "not-numeric" | "bad-percent",
): Result<never> =>
  UNIT_SUFFIXED_NUMBER_PATTERN.test(stripped)
    ? reject("unit-suffix", original)
    : reject(fallback, original);

// negated 서식이면 저장 부호를 뒤집는다: negated ∧ 값≠0 (spec 04 §4). 숫자 kind 판정은
// toStoredNumeric 입구의 isNumericKind가 맡는다. 0은 부호가 없으므로 그대로 둔다(-0 방지).
const flipSignIfNegated = (
  parts: DecimalParts,
  ctx: ValueContext,
): DecimalParts =>
  ctx.negated && !isDecimalZero(parts)
    ? { ...parts, negative: !parts.negative }
    : parts;

/**
 * 숫자 kind(NUMERIC_KINDS) 공통 저장 파이프라인. kind별 차이는 대괄호로 표시한 단계와
 * 파싱 실패 사유(PERCENT만 bad-percent)뿐이다.
 *
 * strip → strict 파싱 → [NUMERIC] integer 제약 → dd 버림 → negated 반전 →
 * [NUMERIC] nonNegative 제약 → [MONETARY] ×10^scale → canonical.
 */
function toStoredNumeric(
  view: string,
  kind: NumericKind,
  ctx: ValueContext,
): Result<string> {
  const stripped = stripInvisibleEdges(view);
  if (stripped === "") return ok("");
  const parsed = parseStrictDecimal(stripped);
  if (parsed === null) {
    return rejectNumericParse(
      stripped,
      view,
      kind === "PERCENT" ? "bad-percent" : "not-numeric",
    );
  }

  // integer 제약은 dd 버림 **이전** 입력값 기준 — 절삭 후 검사하면
  // dd=0일 때 소수가 잘려 나가 제약이 조용히 무력화된다.
  if (
    kind === "NUMERIC" &&
    isIntegerSubKind(ctx.subKind) &&
    parsed.frac.replace(/0+$/, "") !== ""
  ) {
    return reject("integer-required", view);
  }

  let stored = flipSignIfNegated(
    truncateFraction(parsed, ctx.decimalDigits),
    ctx,
  );

  // nonNegative 제약은 반전 **이후** 저장 부호 기준 — 저장되는 값이 음수면 안 된다.
  if (
    kind === "NUMERIC" &&
    isNonNegativeSubKind(ctx.subKind) &&
    stored.negative &&
    !isDecimalZero(stored)
  ) {
    return reject("negative-not-allowed", view);
  }

  if (kind === "MONETARY") stored = shiftPointRight(stored, ctx.scale ?? 0);
  return ok(toCanonicalString(stored));
}

/**
 * 표시 정규형 → 저장 canonical (value-spec 04·05).
 *
 * 숫자 kind는 toStoredNumeric 한 경로: dd 버림(truncate, 0 방향) → negated 부호 반전
 * (0 제외 — 이중 반전 방지를 위해 반전은 이 함수가 유일 지점) → ×10^scale(MONETARY 전용)
 * → canonical(트레일링 제로 제거, -0→0).
 *
 * 목표 동작(spec ⚠현행→목표): NUMERIC subKind가 integer 계열이면 소수 REJECT
 * (integer-required), nonNegative/unsigned 계열이면 음수 REJECT(negative-not-allowed).
 *
 * DURATION: 저장 포맷(PnYnMnD) passthrough / 한국어 표기 → PnYnMnD.
 * BOOLEAN: 소문자화 + true/false 검증. 빈 문자열은 모든 kind에서 no-op.
 */
export function toStored(
  view: IXDViewValue,
  ctx: ValueContext,
): Result<IXDStoredValue> {
  const result = toStoredText(view.text, ctx);
  if (!result.ok) return result;
  // 거절은 Result로 돌려주지만, 통과한 값이 canonical을 어기면 그것은 입력이 아니라
  // 이 함수의 버그다 — storedValue가 throw해 조용히 저장되지 않게 막는다.
  return ok(storedValue(result.value, ctx.kind));
}

// VO는 경계에서만 감싼다 — 내부 변환은 문자열 기반으로 유지한다.
function toStoredText(view: string, ctx: ValueContext): Result<string> {
  if (ctx.kind === "TEXT") return ok(view);
  if (view === "") return ok("");

  if (isNumericKind(ctx.kind)) return toStoredNumeric(view, ctx.kind, ctx);

  switch (ctx.kind) {
    case "DATE": {
      const parsed = parseDateValue(view);
      return parsed ? ok(parsed.dashedValue) : reject("bad-date", view);
    }

    case "DURATION": {
      const storedParts = parseStoredDurationValue(view);
      if (storedParts) return ok(serializeDurationParts(storedParts));
      const editedParts = parseEditedDurationValue(view);
      return editedParts
        ? ok(serializeDurationParts(editedParts))
        : reject("bad-duration", view);
    }

    case "BOOLEAN": {
      const normalized = view.trim().toLowerCase();
      return normalized === "true" || normalized === "false"
        ? ok(normalized)
        : reject("bad-boolean", view);
    }
  }
}
